import type { ComboboxItem } from '@mantine/core';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkPeriodsMeasureSingular } from '../../../../api/work-periods-client/work-periods-client.hooks';
import {
  FlowByPhaseDataOverTime,
  Measure,
  TimeAllocationType,
} from '../../../../api/work-periods-client/work-periods-client.type';
import { formatMonthDate, formatToISODate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { resolvePhaseLabels } from '../../../../helpers/phase-labels/phase-labels';
import type { PhaseLabel } from '../../../../helpers/phase-labels/phase-labels.type';
import { useFlowByPhaseTabStore } from '../../../../store/strategy-store/strategy-store';
import {
  setFlowByPhaseData,
  setFlowByPhases,
  setFlowByPhaseSelectedDate,
} from '../../../../store/strategy-store/strategy-store.actions';
import { useInitiativePerformance } from '../initiative-performance.hooks';
import { getPhasesMetadata } from './flow-by-phase-tab.constants';
import { getLatestDateWithData } from './flow-by-phase-tab.helpers';
import type { ChartDataItem } from './main-section/flow-by-phase-chart/flow-by-phase-chart.type';

/**
 * Hook to fetch and manage flow by phase data for an initiative.
 *
 * This hook fetches flow by phase data over time for the specified initiative.
 * When data is received, it updates the global state with the flow by phase data
 * and sets the selected date to the most recent date that has actual data.
 *
 * @param {string} [epicId] - Optional epic ID to filter data by a specific epic
 * @returns {Object} Object containing loading state
 * @returns {boolean} Object.isLoading - Whether data is currently being fetched
 */
const useFlowByPhaseData = (epicId?: string): { isLoading: boolean } => {
  const initiative = useInitiativePerformance();
  const useGroups = useFlowByPhaseTabStore((state) => state.useGroups);

  const { data, query } = useWorkPeriodsMeasureSingular<{
    flow_by_phase: FlowByPhaseDataOverTime;
    phases?: PhaseLabel[];
  }>({
    payload: {
      start_date:
        formatToISODate(initiative?.effective_start_date ? initiative.effective_start_date : initiative?.start_date) ??
        undefined,
      end_date:
        formatToISODate(initiative?.effective_end_date ? initiative.effective_end_date : initiative?.end_date) ??
        undefined,
      // Send initiative_id whenever we have one: it is what resolves a board, which supplies
      // both the epic's phase mappings and the org's phase-label overrides consumed as
      // `data.phases` below. epic_id narrows the task set on top of it rather than replacing it.
      initiative_id: initiative?.id,
      measure_name: [Measure.FlowByPhase],
      time_allocation_type: TimeAllocationType.Monthly,
      epic_id: epicId || undefined,
      use_groups: useGroups,
    },
    // Without an initiative there is no scope key the resolver recognises, so the response
    // is guaranteed empty. Note this does NOT gate the date window: an initiative can exist
    // with no dates, and `formatToISODate` then omits both params, which the API answers with
    // `phases` only. That is deliberate — the previous raw `dayjs(null).format(...)` sent the
    // literal string "Invalid Date" and the endpoint returned a 500.
    options: { enabled: Boolean(initiative?.id) },
  });

  useEffect(() => {
    if (data?.flow_by_phase) {
      setFlowByPhaseData(data.flow_by_phase);
      setFlowByPhases(data.phases ?? null);

      const latestDate = getLatestDateWithData(data.flow_by_phase);

      if (latestDate) {
        setFlowByPhaseSelectedDate(latestDate);
      }
    } else {
      setFlowByPhaseData(null);
      setFlowByPhases(null);
    }
  }, [data?.flow_by_phase, data?.phases]);

  return { isLoading: query.isFetching };
};

/**
 * Hook that provides date options for the Flow By Phase date selector.
 *
 * This hook processes the flow by phase data to extract dates that have meaningful data
 * (non-null values greater than 0). It formats these dates for display in a dropdown
 * and sorts them in descending order (most recent first).
 *
 * @returns {ComboboxItem[] | []} An array of date options formatted as ComboboxItems,
 *                                or an empty array if no data is available
 */
const useDateOptions = (): ComboboxItem[] | [] => {
  const data = useFlowByPhaseTabStore((state) => state.data);

  if (!data) {
    return [];
  }

  try {
    return Object.entries(data)
      .filter(([_, values]) => {
        if (!values || typeof values !== 'object') {
          return false;
        }

        return Object.entries(values).some(([_, value]) => {
          return value !== null && typeof value === 'number' && value > 0;
        });
      })
      .map(([date]) => ({
        value: date,
        label: formatMonthDate(date),
      }))
      .sort((a, b) => dayjs(b.value).diff(dayjs(a.value)));
  } catch (error) {
    console.error('Error processing date options:', error);
    return [];
  }
};

/**
 * Hook that provides chart data for the Flow By Phase visualization.
 *
 * This hook processes the flow by phase data to create a time series chart dataset.
 * It handles filtering out dates with no meaningful data, sorting by date,
 * and nulling out values after the selected date to show historical progression.
 *
 * @returns {Object} An object containing:
 *   - chartData: Array of processed data points for the chart
 *   - currentDate: The selected date or most recent date with data
 *   - phaseMetadata: Metadata about the phases being displayed
 */
const useChartData = () => {
  const { t } = useTranslation();
  const selectedDate = useFlowByPhaseTabStore((state) => state.selectedDate);
  const data = useFlowByPhaseTabStore((state) => state.data);
  const phases = useFlowByPhaseTabStore((state) => state.phases);

  return useMemo(() => {
    if (!data) {
      return { chartData: [], currentDate: null };
    }

    const allChartData = Object.entries(data)
      .map(([date, values]): ChartDataItem => {
        const valueObj = typeof values === 'object' && values !== null ? (values as Record<string, number | null>) : {};

        return {
          date,
          ...valueObj,
        };
      })
      .filter((item) => {
        return Object.entries(item).some(([key, value]) => {
          if (key === 'date') return false;
          return value !== null && typeof value === 'number' && value > 0;
        });
      })
      .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));

    // Keep all dates but null out values after selected date
    const resolvedLabels = resolvePhaseLabels(phases ?? undefined, t);
    const phasesMetadata = getPhasesMetadata(t, resolvedLabels);
    const processedChartData = allChartData.map((item): ChartDataItem => {
      if (selectedDate && dayjs(item.date).diff(selectedDate) > 0) {
        // Keep the date but null out all phase values
        const nulledItem: ChartDataItem = { date: item.date };
        phasesMetadata.forEach((phase) => {
          nulledItem[`tasks_${phase.id}`] = null;
          nulledItem[`points_${phase.id}`] = null;
        });
        return nulledItem;
      }
      return item;
    });

    // Get the last date with data (most recent)
    const lastDate = selectedDate || allChartData[allChartData.length - 1]?.date;

    return {
      chartData: processedChartData,
      currentDate: lastDate,
      phaseMetadata: phasesMetadata,
    };
  }, [data, phases, selectedDate, t]);
};

/**
 * Hook that filters chart data based on visibility settings.
 *
 * This hook handles filtering for both groups and subgroups mode:
 * - In groups mode: filters based on visiblePhases
 * - In subgroups mode: filters based on selectedSubGroups
 *
 * @param {ChartDataItem[]} chartData - The raw chart data to filter
 * @returns {ChartDataItem[]} Filtered chart data
 */
const useFilteredChartData = (chartData: ChartDataItem[]): ChartDataItem[] => {
  const useGroups = useFlowByPhaseTabStore((state) => state.useGroups);
  const visiblePhases = useFlowByPhaseTabStore((state) => state.visiblePhases);
  const selectedSubGroups = useFlowByPhaseTabStore((state) => state.selectedSubGroups);

  return useMemo(() => {
    // Determine which items should be visible based on mode
    const isVisible = (key: string): boolean => {
      if (useGroups) {
        return visiblePhases[key] ?? false;
      }
      return selectedSubGroups.size === 0 || selectedSubGroups.has(key);
    };

    // Filter chart data to only include visible items
    return chartData.map((dataPoint: ChartDataItem) => {
      const filtered: ChartDataItem = { date: dataPoint.date };

      Object.keys(dataPoint).forEach((key) => {
        if (key === 'date') return;

        const cleanKey = key.replace(/^(tasks_|points_)/, '');
        if (isVisible(cleanKey)) {
          filtered[key] = dataPoint[key];
        }
      });

      return filtered;
    });
  }, [chartData, useGroups, selectedSubGroups, visiblePhases]);
};

export { useChartData, useDateOptions, useFilteredChartData, useFlowByPhaseData };
